import Head from 'next/head';
import { useRouter } from 'next/router';
import Config from '../config/Config';

interface CustomHeadProps {
  pageName?: string;
  description?: string;
  image?: string;
  inline?: boolean; // mostly used for testing
  isPublic?: boolean;
}
function CustomHead({ pageName, description, image, inline, isPublic }: CustomHeadProps) {
  const router = useRouter();
  const fullTitle = `SoleSavy | ${pageName}`;
  const defaultImage = `${Config.APP_URL}/favicon-192.png`;
  let metaImage = image || defaultImage;

  if (metaImage && metaImage.startsWith('/')) {
    metaImage = `${Config.APP_URL}${metaImage}`;
  }

  const canonicalUrl = `${Config.APP_URL}${router?.asPath || ''}`;

  const tags = (
    <>
      <link rel="icon" type="image/svg+xml" href="/ss_logo.svg" />
      <link rel="apple-touch-icon" href="/favicon-180.png" />
      <link rel="apple-touch-icon" sizes="72x72" href="/favicon-76.png" />
      <link rel="apple-touch-icon" sizes="144x144" href="/favicon-144.png" />
      <link rel="apple-touch-icon" sizes="180x180" href="/favicon-180.png" />
      <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet" />
      <title data-testid="title">{fullTitle}</title>
      <meta name="robots" content={isPublic ? 'index, follow' : 'noindex'} />
      <meta data-testid="description" name="description" content={description} />
      {image && <link rel="preload" as="image" href={image} />}
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, maximum-scale=1.0, minimum-scale=0.86"
      />
      <link rel="icon" href="/favicon.ico" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      {metaImage && <meta data-testid="og-image" property="og:image" content={metaImage} />}
      <meta property="og:locale" content="en_US" />
      <meta property="og:site_name" content="SoleSavy Releases" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      {metaImage && <meta data-testid="twitter-image" name="twitter:image" content={metaImage} />}
    </>
  );
  return inline ? tags : <Head>{tags}</Head>;
}

CustomHead.defaultProps = {
  pageName: 'Something went wrong',
  description:
    'Access tools and resources to grow your collection, explore the education center, and manage your account.',
  image: '',
  inline: false,
  isPublic: false,
};

export default CustomHead;
